import type { CSSProperties, ReactNode } from 'react';
import { cx } from './cx.ts';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps {
  /** Display name: accessible name and source of the initials. */
  name: string;
  /** Child identity slot 1..6 (§5.3); omit for adults (neutral). */
  slot?: number;
  size?: AvatarSize;
  /** `fill` in light mode; dark/night CSS switches to the ring style automatically. */
  variant?: 'fill' | 'ring';
  /** Seventh+ child: dashed ring and two-letter monogram. */
  repeated?: boolean;
  /** The name is adjacent: hide from assistive tech. */
  decorative?: boolean;
  /** Photo URL; initials remain the fallback. */
  src?: string;
  status?: ReactNode;
  className?: string;
}

/** First letter of the first one or two words (by code point, so Thaana/Arabic stay intact). */
export function initials(name: string, count: 1 | 2 = 1): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.slice(0, count).map((w) => Array.from(w)[0] ?? '');
  if (count === 2 && letters.length === 1) letters.push(Array.from(words[0] ?? '')[1] ?? '');
  return letters.join('').toLocaleUpperCase();
}

export function Avatar({ name, slot, size = 'md', variant = 'fill', repeated, decorative, src, status, className }: AvatarProps) {
  const s = slot ? (((slot - 1) % 6) + 6) % 6 + 1 : undefined;
  const style = s ? ({ '--avatar-c': `var(--child-${s})`, '--avatar-ink': `var(--child-${s}-ink)` } as CSSProperties) : undefined;
  return (
    <span
      className={cx('yk-avatar', `yk-avatar--${size}`, `yk-avatar--${variant}`, s ? `yk-avatar--child-${s}` : undefined, repeated && 'yk-avatar--repeated', className)}
      style={style}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative || undefined}
    >
      {src ? <img src={src} alt="" className="yk-avatar__img" /> : <span className="yk-avatar__initials">{initials(name, repeated ? 2 : 1)}</span>}
      {status ? <span className="yk-avatar__status">{status}</span> : null}
    </span>
  );
}
